/**
 * Users sagas
 */
import { call, put, takeLatest } from "redux-saga/effects";
import { toast } from "react-toastify";

import { httpRequest } from "../../services/http-service";
import { endPoints } from "../../services/endpoints";
import {
  fetchUsers,
  fetchUser,
  createUser,
  updateUserAction,
  deleteUser,
  setUsers,
  setCurrentUser,
  addUser,
  updateUser,
  removeUser,
  setLoading,
  setError,
} from "./usersSlice";
import { presentLoader, dismissLoader } from "../../store/slices/commonSlice";

/**
 * Fetch all users saga
 */
function* fetchUsersSaga(action: ReturnType<typeof fetchUsers>): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const params: { page?: number; limit?: number } = {
      page: 1,
      limit: 10,
    };

    if (action.payload) {
      if (action.payload.page !== undefined) params.page = action.payload.page;
      if (action.payload.limit !== undefined) params.limit = action.payload.limit;
    }

    const response = yield call(httpRequest.get, endPoints.users.getAll, {
      params,
    });

    if (response?.success) {
      // Map backend user data to frontend format
      const responseData = response.data || response;
      if (Array.isArray(responseData)) {
        const users = responseData.map((user: any) => ({
          ...user,
          displayName: user.displayName || user.username || user.email,
          uid: user._id || user.id || user.uid,
        }));
        yield put(setUsers(users));
      } else {
        const users = (responseData.data || []).map((user: any) => ({
          ...user,
          displayName: user.displayName || user.username || user.email,
          uid: user._id || user.id || user.uid,
        }));
        yield put(setUsers({ data: users, ...responseData }));
      }
    } else {
      toast.error("Failed to fetch users");
      yield put(setError("Failed to fetch users"));
    }
  } catch (error: any) {
    const message = error?.message || "Failed to fetch users";
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Fetch single user saga
 */
function* fetchUserSaga(
  action: ReturnType<typeof fetchUser>
): Generator<any, void, any> {
  try {
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.get,
      endPoints.users.getById(action.payload)
    );

    if (response?.success) {
      yield put(setCurrentUser(response.data || response));
    } else {
      toast.error("Failed to fetch user");
      yield put(setError("Failed to fetch user"));
    }
  } catch (error: any) {
    const message = error?.message || "Failed to fetch user";
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
  }
}

/**
 * Create user saga
 */
function* createUserSaga(
  action: ReturnType<typeof createUser>
): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.post,
      endPoints.users.create,
      action.payload
    );

    if (response?.success) {
      const newUser = {
        ...response.data || response,
        displayName: action.payload.displayName || action.payload.username || action.payload.email,
        uid: response.data?._id || response.data?.id || response.data?.uid,
      };
      yield put(addUser(newUser));
      toast.success("user is created");
    } else {
      toast.error("Failed to create user");
      yield put(setError("Failed to create user"));
    }
  } catch (error: any) {
    const message = error?.message || "Failed to create user";
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Update user saga
 */
function* updateUserSaga(
  action: ReturnType<typeof updateUserAction>
): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.put,
      endPoints.users.update(action.payload.id),
      action.payload.data
    );

    if (response?.success) {
      const updatedUser = { ...response.data, _id: action.payload.id };
      yield put(updateUser(updatedUser));
      toast.success("user is updated");
    } else {
      toast.error("Failed to update user");
      yield put(setError("Failed to update user"));
    }
  } catch (error: any) {
    const message = error?.message || "Failed to update user";
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

/**
 * Delete user saga
 */
function* deleteUserSaga(
  action: ReturnType<typeof deleteUser>
): Generator<any, void, any> {
  try {
    yield put(presentLoader());
    yield put(setLoading(true));

    const response = yield call(
      httpRequest.delete,
      endPoints.users.delete(action.payload)
    );

    if (response?.success || response) {
      yield put(removeUser(action.payload));
      toast.success("user is deleted");
    } else {
      toast.error("Failed to delete user");
      yield put(setError("Failed to delete user"));
    }
  } catch (error: any) {
    const message = error?.message || "Failed to delete user";
    toast.error(message);
    yield put(setError(message));
  } finally {
    yield put(setLoading(false));
    yield put(dismissLoader());
  }
}

export function* usersSagas(): Generator<any, void, any> {
  yield takeLatest(fetchUsers, fetchUsersSaga);
  yield takeLatest(fetchUser, fetchUserSaga);
  yield takeLatest(createUser, createUserSaga);
  yield takeLatest(updateUserAction, updateUserSaga);
  yield takeLatest(deleteUser, deleteUserSaga);
}
